// #727/#730 -- the open-state rule, mirrored from flow/scheduling/open_states.py:
// a non-paying client who lives in an open state (FL, RI) books phone (plus the
// AEP survey in season) with the allow-listed advisors only. Both schedulers
// read the open-state list once from the public endpoint and apply the same
// predicate and method rule the backend applies at /book/. The backend is the
// authority; this exists so the UI never offers a tile or an advisor the
// server will refuse. Each scheduler decides "non-paying" from what it knows
// (the admin's referral flags, the wizard's group verification).
let cache = null

export async function fetchOpenStates (api) {
    if (cache) return cache
    try {
        const resp = await api.getOpenStates()
        cache = Array.isArray(resp?.states) ? resp.states.map(s => String(s).toUpperCase()) : []
        return cache
    } catch (e) {
        // Fail closed like the backend, but don't remember the failure: a blip
        // on page load must not switch FL/RI off for the rest of the session.
        return []
    }
}

export function resetOpenStatesCache () { cache = null }

// #730: the state alone decides -- the referral source no longer matters.
export function isOpenState (state, states) {
    if (!state) return false
    return (states || []).includes(String(state).trim().toUpperCase())
}

export function openStateMethods (aep) { return aep ? ['phone', 'survey'] : ['phone'] }
export function dpcMethods (aep) { return aep ? ['phone', 'survey'] : ['phone'] }

// The FL / RI designation (Dien + Kalena, 2026-10-05). The backend computes
// `c_open_state` on the agent survey payload and on /search rows from the one
// predicate (flow/scheduling/open_states.py); the client header, /search and
// the legacy scheduler card show the map marker from it, ahead of the email
// client's star and the self-serve hand. Only an explicit true counts: an older
// payload, or one from a caller that did not judge the rule, is not marked.
export function isOpenStateClient (record) {
    return record?.c_open_state === true
}

function residents (states) {
    return states && states.length ? states.join(' / ') : 'Open-state'
}

export function openStateClientTitle (states) {
    return `${residents(states)} resident: phone consultations (plus the AEP survey in season) with the designated advisors only`
}

// /search's Service Type dropdown: the old tiers, then the covered clients.
export function serviceTypeOptions (states) {
    return [
        { text: 'Any Service Type', value: null },
        { text: 'Fullserve+ & Fullserve', value: 'fullserve_and_plus' },
        { text: 'Fullserve+ Only', value: 'fullserve_plus' },
        { text: 'Fullserve Only', value: 'fullserve' },
        { text: 'Selfserve', value: 'selfserve' },
        { text: `${residents(states)} residents`, value: 'open_state' },
    ]
}

// The store's usStates getter can't name a state: the API returns bare codes
// and the getter maps each to { text: code, value: code }.
const STATE_NAMES = {
    AK: 'Alaska',
    AL: 'Alabama',
    AR: 'Arkansas',
    AZ: 'Arizona',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DC: 'District of Columbia',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    IA: 'Iowa',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    MA: 'Massachusetts',
    MD: 'Maryland',
    ME: 'Maine',
    MI: 'Michigan',
    MN: 'Minnesota',
    MO: 'Missouri',
    MS: 'Mississippi',
    MT: 'Montana',
    NC: 'North Carolina',
    ND: 'North Dakota',
    NE: 'Nebraska',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NV: 'Nevada',
    NY: 'New York',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VA: 'Virginia',
    VT: 'Vermont',
    WA: 'Washington',
    WI: 'Wisconsin',
    WV: 'West Virginia',
    WY: 'Wyoming',
}

// Every code the admin "Open states" box can offer, with its name.
export const US_STATE_CODES = Object.keys(STATE_NAMES)
export function stateName (code) { return STATE_NAMES[String(code || '').trim().toUpperCase()] || String(code || '') }

// 'Florida or Rhode Island' -- for copy that names the open states.
export function stateNames (codes) {
    const names = (codes || []).map((c) => {
        const code = String(c).trim().toUpperCase()
        return STATE_NAMES[code] || code
    })
    if (names.length <= 1) return names.join('')
    return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}
